import React, { useRef, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import swal from "sweetalert";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  updateCallStatus,
  updateCurrentCall,
} from "../store/features/campaignsLauchedSlice";
import { format } from "date-fns";

/* =========================================================================
   Estados visibles: Pending → Dialing → Call End
   (Seguimos contando solo nuestras llamadas y respetando numLines)
   ========================================================================= */

const CALL_STATUS = {
  PENDING: "Pending",
  DIALING: "Dialing",
  CANCELLED: "Call End", // final visible (como te gustaba antes)
  // internos (para mapeo y lógica, no los mostramos):
  RINGING: "Ringing",
  IN_CALL: "In Call",
  BUSY: "Busy",
  NO_ANSWER: "No Answer",
  FAILED: "Failed",
  PAUSED: "Paused",
};

const DEBUG = false;
const log = (...a) => DEBUG && console.log("[CallProcessor]", ...a);

// Normaliza call_id a string
const cidOf = (x) => (x == null ? "" : String(x));

export default function CallProcessor({ id_launchedCampaing }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const pbx = useSelector((s) => s.pbx.value);
  const launched = useSelector((s) => s.campaingsLaunched.value);

  const intervalValue = 6; // segundos entre polls
  const [numLines, setNumLines] = useState(1);
  const [isPaused, setIsPaused] = useState(true);
  const [idCampaignLaunched, setIdCampaignLaunched] = useState(0);

  const [destinatarios, setDestinatarios] = useState([]);
  const [id_campania, setId_campania] = useState();
  const [numeroIvr, setNumeroIvr] = useState();

  const currentIndex = useRef(0);

  // Solo mis CIDs
  const myCids = useRef([]);              // string[]
  const activeCalls = useRef(new Map());  // cid -> startedAt
  const lastSeen = useRef(new Map());     // cid -> ts último /query

  const isCancelled = useRef(false);
  const callInCurse = useRef(false);
  const endingCampaing = useRef(false);
  const inFlight = useRef(false);         // candado para no duplicar makeCall

  // Reintentos por contacto (máx 2)
  const retryPerIndex = useRef(new Map()); // idx -> count

  // Heurísticas
  const GRACE_MS = 2000;     // margen para considerar que terminó
  const HARD_TTL = 15 * 60_000;

  useEffect(() => {
    const c = launched.find((x) => x.idCampaingLunched === id_launchedCampaing);
    if (c) {
      setDestinatarios(c.calls);
      setNumeroIvr(c.ivr_number);
      setId_campania(c.idcampaign);
    } else {
      console.log("Campaña lanzada no encontrada con id:", id_launchedCampaing);
    }
  }, [launched, id_launchedCampaing]);

  const handleNumLinesChange = (e) => {
    const n = Math.max(1, parseInt(e.target.value, 10) || 1);
    setNumLines(n);
  };

  const validateErrorCodes = (errorCode) => {
    if (errorCode === 10004) {
      return swal({
        title: "An error occurred",
        text: "Phone system timeout, please reconnect the phone system.",
        icon: "error",
        buttons: {
          redirect: { text: "Ir a Central Telefonica", value: "redirect" },
          cancel: "Cancel",
        },
      }).then((v) => v === "redirect" && navigate("/pbx-management"));
    }
    return swal({
      title: "An error occurred",
      text: "Unidentified error",
      icon: "error",
    });
  };

  // Mapea estado de miembro para detectar ring/talk (solo para control interno)
  const pbxMemberToPhase = (m) => {
    const raw = (
      m?.state ||
      m?.status ||
      m?.outbound?.status ||
      m?.inbound?.status ||
      ""
    ).toString().toLowerCase();

    if (raw.includes("ring") || raw.includes("alert") || raw.includes("early") || raw.includes("progress") || raw.includes("trying"))
      return CALL_STATUS.RINGING;

    if (raw.includes("talk") || raw.includes("answer") || raw.includes("connect") || raw.includes("up"))
      return CALL_STATUS.IN_CALL;

    if (raw.includes("busy") || raw.includes("congestion")) return CALL_STATUS.BUSY;
    if (raw.includes("no") && raw.includes("answer")) return CALL_STATUS.NO_ANSWER;
    if (raw.includes("fail") || raw.includes("carrier")) return CALL_STATUS.FAILED;

    return null;
  };

  // Poll al PBX (sincroniza final de mis CIDs y capacidad)
  const checkCall = useCallback(async () => {
    try {
      let response;
      let api = pbx.domain + "/openapi/v1.0/call/query?access_token=" + pbx.token;

      if (pbx.model === "S-Cloud") {
        api = pbx.domain + "/api/v2.0.0/call/query?token=" + pbx.token;
        response = await axios.post(api);
      } else {
        response = await axios.get(api);
      }

      let status, Calls, errmsg, data;
      if (pbx.model === "S-Cloud") ({ status, Calls } = response.data);
      else ({ errmsg, data } = response.data);

      const ok = status ?? errmsg;
      if (ok !== "Success" && ok !== "SUCCESS") {
        throw new Error(response?.data?.errmsg || "La respuesta no fue exitosa");
      }

      const calls = Array.isArray(Calls ?? data) ? (Calls ?? data) : [];
      const now = Date.now();
      const cidSet = new Set(calls.map((c) => cidOf(c?.call_id ?? c?.id)));

      // Mantén vivos los que aparezcan
      const mine = new Set(myCids.current);
      for (const call of calls) {
        const cid = cidOf(call?.call_id ?? call?.id);
        if (!cid || !mine.has(cid)) continue;

        if (!activeCalls.current.has(cid)) activeCalls.current.set(cid, now);
        lastSeen.current.set(cid, now);

        // (Opcional) podríamos promover a "Call Made", pero tú lo quieres simple
        // así que no actualizamos a "Call Made" ni "Ringing".
      }

      // Detecta final de mis CIDs (desaparecieron del query)
      const finished = [];
      for (const [cid, startedAt] of activeCalls.current.entries()) {
        if (!cidSet.has(cid)) {
          const last = lastSeen.current.get(cid) ?? startedAt;
          const ageSinceLast = now - last;
          const total = now - startedAt;

          if (ageSinceLast >= GRACE_MS || total >= HARD_TTL) {
            finished.push(cid);
          }
        }
      }

      for (const cid of finished) {
        activeCalls.current.delete(cid);
        lastSeen.current.delete(cid);
        await dispatch(
          updateCallStatus({
            idCampaingLunched: id_launchedCampaing,
            idCall: cid,
            newStatus: CALL_STATUS.CANCELLED, // Call End (como antes)
          })
        );
      }

      // Capacidad: Cloud cuenta por miembros de MIS CIDs; On-Prem por CIDs
      let effectiveCount;
      if (pbx.model === "S-Cloud") {
        const mineSet = new Set(myCids.current);
        let memberCount = 0;
        for (const call of calls) {
          const cid = cidOf(call?.call_id ?? call?.id);
          if (!cid || !mineSet.has(cid)) continue;
          const members = Array.isArray(call.members) ? call.members : [];
          memberCount += members.length;
        }
        effectiveCount = memberCount;
      } else {
        effectiveCount = activeCalls.current.size;
      }

      callInCurse.current = effectiveCount >= numLines;
      log("cap:", { effectiveCount, numLines, callInCurse: callInCurse.current });
    } catch (err) {
      console.error("Error en checkCall:", err);
      swal("Ocurrió un error", err?.message || "Por favor inténtelo más tarde", "error");
      setIsPaused(true);
    }
  }, [numLines, pbx.domain, pbx.token, pbx.model, dispatch, id_launchedCampaing]);

  // Marca y NO avanza índice si no hay SUCCESS+call_id. Reintenta mismo contacto.
  const makeCall = useCallback(
    async (idx, idContact, phoneNumber) => {
      try {
        let tries = retryPerIndex.current.get(idx) || 0;

        if (callInCurse.current) {
          log("blocked (capacity)", { idx, numLines });
          return false;
        }

        callInCurse.current = true;

        let api_call = pbx.domain + "/openapi/v1.0/call/dial?access_token=" + pbx.token;
        let data_call = {
          caller: numeroIvr,
          callee: pbx.ruta + phoneNumber,
          dial_permission: pbx.extension,
          auto_answer: "no",
        };
        if (pbx.model === "S-Cloud") {
          api_call = pbx.domain + "/api/v2.0.0/call/dial?token=" + pbx.token;
          data_call = { caller: numeroIvr, callee: pbx.ruta + phoneNumber, autoanswer: "no" };
        }

        const resp = await axios.post(api_call, data_call);

        if (resp.data?.errmsg === "SUCCESS" && resp.data?.call_id) {
          const cid = cidOf(resp.data.call_id);

          myCids.current.push(cid);
          activeCalls.current.set(cid, Date.now());

          await dispatch(
            updateCurrentCall({
              idCall: cid,
              idCampaingLunched: id_launchedCampaing,
              idContact,
              newStatus: CALL_STATUS.DIALING,
              date: format(new Date(), "yyyy/MM/dd HH:mm:ss"),
            })
          );

          // con éxito real, resetea reintentos de ese índice
          retryPerIndex.current.delete(idx);

          // bloqueo: se ajustará en el próximo poll
          callInCurse.current = activeCalls.current.size >= numLines;
          return true;
        }

        // fallo (sin call_id o sin SUCCESS) → NO avanzar índice
        callInCurse.current = false;

        tries += 1;
        retryPerIndex.current.set(idx, tries);

        if (resp.data?.errcode) validateErrorCodes(resp.data.errcode);
        // Opcional: si quieres marcar en UI algo como Failed, podrías hacerlo aquí

        // Si excede reintentos, ya avanzamos para no trabarnos
        if (tries >= 2) {
          log("max retries reached, skipping idx", idx);
          return true; // avanza al siguiente si ya intentamos 2 veces
        }

        return false; // reintentar el mismo
      } catch (err) {
        console.error("Error en makeCall:", err);
        callInCurse.current = false;

        // Reintentos en error de red
        const tries = (retryPerIndex.current.get(idx) || 0) + 1;
        retryPerIndex.current.set(idx, tries);

        swal("Ocurrió un error", err?.message || "Por favor inténtelo más tarde", "error");
        // si ya intentamos 2, avanzamos
        return tries >= 2;
      }
    },
    [dispatch, id_launchedCampaing, numeroIvr, pbx.domain, pbx.token, pbx.ruta, pbx.extension, pbx.model, numLines]
  );

  const processCalls = useCallback(async () => {
    if (inFlight.current) return; // evita reentradas
    inFlight.current = true;

    try {
      if (currentIndex.current >= destinatarios.length) {
        if (activeCalls.current.size === 0) {
          setIsPaused(true);
          swal("The campaign has been completed", { icon: "success" });
          return;
        }
        endingCampaing.current = true;
      }

      if (!isPaused && !isCancelled.current) {
        await checkCall();
        if (!callInCurse.current && !endingCampaing.current) {
          const idx = currentIndex.current;
          const { id, num } = destinatarios[idx];
          const ok = await makeCall(idx, id, num);
          if (ok) {
            currentIndex.current++;
            await new Promise((r) => setTimeout(r, 1000)); // pausa breve
          }
          // si no ok, reintentará el mismo idx en el próximo tick
        }
      }
    } finally {
      inFlight.current = false;
    }
  }, [destinatarios, isPaused, checkCall, makeCall]);

  useEffect(() => {
    if (!isPaused && !isCancelled.current) {
      const it = setInterval(processCalls, intervalValue * 1000);
      return () => clearInterval(it);
    }
  }, [isPaused, intervalValue, processCalls]);

  const handleCampaignAction = async (action) => {
    try {
      if (action === "start") {
        setIdCampaignLaunched(id_campania);
        setIsPaused(false);
        isCancelled.current = false;
      } else if (action === "cancel") {
        swal({
          title: "End campaign",
          text: "Are you sure to finish the campaign?",
          icon: "warning",
          buttons: true,
          dangerMode: true,
        }).then((willDelete) => {
          if (willDelete) {
            isCancelled.current = true;
            setIsPaused(true);
            navigate("/select-campaign");
          }
        });
      } else {
        setIsPaused(action === "pause");
      }
    } catch (err) {
      console.error(`Error en ${action}Campaign:`, err);
      swal("Ocurrió un error", "Por favor inténtelo más tarde", "error");
    }
  };

  return (
    <div className="text-right d-flex flex-wrap justify-content-end align-items-center">
      <form className="mb-2 mr-sm-1 d-flex align-items-center">
        <label className="mr-2 mb-0" style={{ whiteSpace: "nowrap" }}>
          Number of Lines:
        </label>
        <input
          type="number"
          className="form-control form-control-sm mr-2 bg-white border border-dark"
          style={{ width: "70px" }}
          value={numLines}
          onChange={handleNumLinesChange}
          min="1"
        />
      </form>
      <button
        type="button"
        className="btn mb-2 btn-success text-white mr-1"
        disabled={idCampaignLaunched !== 0}
        onClick={() => handleCampaignAction("start")}
      >
        <span className="fe fe-phone-forwarded fe-16 mr-1"></span>
        Start Calls
      </button>
      <button
        className="btn mb-2 btn-warning text-white mr-1"
        disabled={isPaused}
        onClick={() => handleCampaignAction("pause")}
      >
        <span className="fe fe-pause fe-16 mr-1"></span>
        Pause
      </button>
      <button
        className="btn mb-2 btn-info text-white mr-1"
        disabled={!isPaused}
        onClick={() => handleCampaignAction("resume")}
      >
        <span className="fe fe-play fe-16 mr-1"></span>
        Resume
      </button>
      <button
        className="btn mb-2 btn-danger text-white mr-1"
        onClick={() => handleCampaignAction("cancel")}
      >
        <span className="fe fe-stop-circle fe-16 mr-1"></span>
        Cancel
      </button>
    </div>
  );
}

CallProcessor.propTypes = {
  id_launchedCampaing: PropTypes.number.isRequired,
};

CallProcessor.defaultProps = {
  id_launchedCampaing: 0,
};
